import {DefaultFoundationTheme, Integer} from 'platform/foundation';
import styled from 'styled-components';

import {Nullish} from 'shared/web';

import {quillThemeOverrides} from './quillTheme';

export type StyledQuillWrapperProps = {
  $isFocused?: boolean;
  $isDisabled?: boolean;
  $isInvalid?: boolean;
  $isScrollable?: boolean;
  $rows?: Integer;
  $minRows?: Integer | Nullish;
  $maxRows?: Integer | Nullish;
};

export const StyledQuillWrapper = styled.div<StyledQuillWrapperProps>`
  position: relative;

  .ql-container {
    /* A flex column gives the editor a definite box to fill: Quill's base
     * '.ql-editor { height: 100% }' otherwise collapses to content height
     * (the container only sets min-height), leaving dead, unclickable space
     * below the text (T20-125858). */
    display: flex;
    flex-direction: column;
    min-height: ${({theme, $rows, $minRows}) =>
      `max(150px, ${getEditorHeight(theme, $minRows ?? $rows ?? 6)})`};
    /* Cap the height so long content scrolls instead of stretching the editor.
     * rows acts as a fixed height (like a textarea) when no explicit maxRows is
     * given; without either, the editor grows freely. */
    max-height: ${({theme, $maxRows, $rows}) =>
      $maxRows ? getEditorHeight(theme, $maxRows) : $rows ? getEditorHeight(theme, $rows) : 'none'};
    /* Clip only vertically: leave overflow-x default so nothing horizontal is
     * unexpectedly cut off (the editor owns the vertical scroll below). */
    overflow-y: hidden;

    .ql-editor {
      /* Fill the whole container so the entire box is a click target. */
      flex: 1 1 auto;
      /* Own the vertical scroll, but only when the consumer opts in via
       * isScrollable; otherwise overflow is clipped (the legacy contract). */
      overflow-y: ${({$isScrollable}) => ($isScrollable ? 'auto' : 'hidden')};
      resize: none;
    }
  }

  ${quillThemeOverrides}
`;

function getEditorHeight(theme: DefaultFoundationTheme, rows: Integer) {
  return `calc(${rows} * ${theme.lineHeights.text.small} + ${theme.getSize(4)})`;
}
